/**
 * Turning the Iris step's wired branches into stored handoff (escalation)
 * rules, and back.
 *
 * WHY THIS EXISTS. Handoff used to be a form: name a rule, tick some triggers,
 * build a ring ladder inside the form, pick a fallback from a dropdown. Every
 * one of those already exists on the call-flow canvas as a node — "Ring people"
 * IS a ladder, "Voicemail"/"Hang up" IS a fallback — so the form was a second
 * way to say what the canvas already says, with its own concepts (a rule name)
 * that appear nowhere else in the product.
 *
 * So the canvas became the editor. This module is the translation layer.
 *
 * WHERE THE TRUTH LIVES. Not here. The calling service executes a ladder handed
 * to it in the escalate webhook body, resolved by ai-operations from stored
 * escalation rules — so the rules remain the storage, and saving a flow
 * materialises them from its Iris branches. Keeping the backend contract intact
 * is deliberate: this is a change to how handoff is EDITED, not to how a call
 * is routed at 2am.
 *
 * Everything here is pure so the mapping can be tested without a canvas, an
 * API, or a browser.
 */
import {
  HANDOFF_TRIGGERS,
  type CallFlowStep,
  type HandoffTrigger,
} from '@/lib/voice/call-flow-types';
import {
  DEFAULT_RING_SECONDS,
  MAX_RING_SECONDS,
  MIN_RING_SECONDS,
  type CreateEscalationRulePayload,
  type EscalationFallback,
  type EscalationRule,
  type EscalationRung,
  type EscalationTrigger,
} from '@/lib/ai/escalation-rules';

/** Branch key → the trigger name ai-operations stores. */
export const ESCALATION_TRIGGER_BY_BRANCH: Record<HandoffTrigger, EscalationTrigger> = {
  human_request: 'HUMAN_REQUEST',
  negative_sentiment: 'NEGATIVE_SENTIMENT',
  pricing: 'PRICING',
  no_slots: 'NO_SLOTS',
  low_confidence: 'LOW_CONFIDENCE',
  reschedule_threshold: 'RESCHEDULE_THRESHOLD',
};

export const BRANCH_BY_ESCALATION_TRIGGER = Object.fromEntries(
  Object.entries(ESCALATION_TRIGGER_BY_BRANCH).map(([branch, trigger]) => [trigger, branch]),
) as Record<EscalationTrigger, HandoffTrigger>;

const isHandoffTrigger = (key: string): key is HandoffTrigger =>
  (HANDOFF_TRIGGERS as readonly string[]).includes(key);

/**
 * A rule this module owns, tagged so a later save can tell the rules it wrote
 * from ones a human wrote elsewhere. Names are generated, never typed — the
 * "Call it" field is gone, and a name nobody chose must not look like one
 * somebody did.
 */
export const FLOW_RULE_PREFIX = 'Call flow · ';

export function isFlowOwnedRule(rule: { name: string }): boolean {
  return rule.name.startsWith(FLOW_RULE_PREFIX);
}

/**
 * What a rule materialised from `branch` is called in storage.
 *
 * The trailing `[flowId]` is the OWNERSHIP KEY, and it is an id rather than the
 * flow's name for two reasons found in review (#2880): renaming a flow left its
 * old rules behind for ever, because nothing afterwards recognised them; and a
 * name-substring test let a short name ("Main") claim another flow's rules
 * ("Main line"). Rules are tried in priority order, so either one leaves a
 * stale or foreign rule winning after a save.
 *
 * The name in the middle is for humans and may drift freely — a rename now
 * rewrites it in place, because the identity never depended on it.
 */
export function flowRuleName(
  flowId: string,
  flowName: string,
  branch: HandoffTrigger,
): string {
  return `${FLOW_RULE_PREFIX}${flowName} · ${branch} [${flowId}]`;
}

/** The flow that owns this rule, or null when no flow does. */
export function flowRuleOwner(rule: { name: string }): string | null {
  if (!isFlowOwnedRule(rule)) return null;
  return /\[([^\][]+)\]\s*$/.exec(rule.name)?.[1] ?? null;
}

/**
 * The reason a stored rule answers, read from the trigger rather than the name.
 * Only single-trigger rules have one: a rule covering several is not something
 * one branch can stand for.
 */
export function flowRuleBranch(rule: { triggerTypes: EscalationTrigger[] }): HandoffTrigger | null {
  if (rule.triggerTypes.length !== 1) return null;
  return BRANCH_BY_ESCALATION_TRIGGER[rule.triggerTypes[0]] ?? null;
}

// ─── Flow → rules ────────────────────────────────────────────────────────────

const clampSeconds = (n: number | null | undefined): number => {
  if (typeof n !== 'number' || !Number.isFinite(n)) return DEFAULT_RING_SECONDS;
  return Math.min(MAX_RING_SECONDS, Math.max(MIN_RING_SECONDS, Math.round(n)));
};

/**
 * The ring ladder a `ring_users` step describes.
 *
 * `userIds` is the 1.0 field and `notifi2UserIds` the one this app writes; a
 * flow can carry either, so both are read. A group naming nobody is dropped
 * rather than sent — an empty rung would ring nothing and stall the ladder on
 * a step that can never be answered.
 */
export function ladderFromRingStep(step: CallFlowStep): EscalationRung[] {
  const fallbackSeconds = clampSeconds(step.ringTimeoutSecs);
  return (step.groups ?? [])
    .map((g) => ({
      userIds: [...(g.notifi2UserIds ?? []), ...(g.userIds ?? [])],
      externalNumbers: [],
      ringSeconds: clampSeconds(g.seconds ?? fallbackSeconds),
    }))
    .filter((rung) => rung.userIds.length > 0);
}

/**
 * Where the call goes when the ladder runs out — read from the step wired to
 * the ring step's `no_answer` branch.
 *
 * Anything that is not an explicit hang-up is voicemail, which is both the
 * stored default and the safer read: an unwired or unrecognised outcome should
 * take a message, not drop the customer.
 */
export function fallbackFromStep(step: CallFlowStep | undefined): EscalationFallback | null {
  if (!step) return 'VOICEMAIL';
  if (step.type === 'hangup') return 'HANGUP';
  if (step.type === 'voicemail') return 'VOICEMAIL';
  // NULL MEANS "this cannot be said", not "voicemail". Collapsing every other
  // target to VOICEMAIL turned `ring Dana → forward to the answering service`
  // into `ring Dana → voicemail` in the stored rule — a behaviour change, not a
  // display mismatch. The blocking problem the canvas raises for this only gates
  // PUBLISH; a save would still have written the wrong rule. Review, #2880.
  return null;
}

export interface MaterialisedRule extends CreateEscalationRulePayload {
  /** The branch it came from, so the UI can point at the node that owns it. */
  branch: HandoffTrigger;
  /** The ring step it read the ladder off — how adoption identifies a drawing. */
  ringStepId: string;
}

/**
 * Every handoff rule the flow's Iris steps describe.
 *
 * A branch wired to something that is not a `ring_users` step is skipped rather
 * than guessed at: "if they ask for a person → Voicemail" is a legitimate thing
 * to draw, but it is not a ladder, and inventing an empty one would turn it
 * into "ring nobody" instead of the account default.
 */
export function rulesFromFlow(
  flowId: string,
  flowName: string,
  steps: readonly CallFlowStep[],
): MaterialisedRule[] {
  const byId = new Map(steps.map((s) => [s.id, s]));
  const out: MaterialisedRule[] = [];
  const seen = new Set<HandoffTrigger>();

  // A reason two Iris steps both claim is AMBIGUOUS, and none of it is written.
  // Taking the first left the canvas showing two destinations while storage had
  // one, and the operator no way to tell which. The canvas raises a blocking
  // problem for this, but that only gates PUBLISH — a save materialised the
  // first regardless. No rule rings the whole account, which is defined; a rule
  // for the branch nobody can identify is a guess. Review, #2880.
  const contested = new Set<HandoffTrigger>();
  const claimedOnce = new Set<HandoffTrigger>();
  for (const step of steps) {
    if (step.type !== 'ai_receptionist') continue;
    for (const key of Object.keys(step.next ?? {})) {
      if (!isHandoffTrigger(key)) continue;
      if (claimedOnce.has(key)) contested.add(key);
      claimedOnce.add(key);
    }
  }

  for (const step of steps) {
    if (step.type !== 'ai_receptionist') continue;

    for (const [key, targetId] of Object.entries(step.next ?? {})) {
      if (!isHandoffTrigger(key)) continue;
      if (seen.has(key) || contested.has(key)) continue;

      const ring = byId.get(targetId);
      if (ring?.type !== 'ring_users') continue;

      const targets = ladderFromRingStep(ring);
      if (targets.length === 0) continue;

      // A fallback the rule model cannot express means NO rule, which falls back
      // to ringing the whole account — a defined behaviour — rather than a rule
      // that quietly says something the operator did not draw.
      const fallback = fallbackFromStep(byId.get(ring.next?.no_answer ?? ''));
      if (fallback === null) continue;

      seen.add(key);
      out.push({
        branch: key,
        ringStepId: ring.id,
        name: flowRuleName(flowId, flowName, key),
        triggerTypes: [ESCALATION_TRIGGER_BY_BRANCH[key]],
        targets,
        fallback,
        enabled: true,
      });
    }
  }

  return out;
}

/** Reasons wired to something that cannot become a ladder — worth saying so. */
export function danglingHandoffBranches(steps: readonly CallFlowStep[]): HandoffTrigger[] {
  const out: HandoffTrigger[] = [];
  // ASKS THE MATERIALISER what it will actually write, rather than re-deriving
  // its conditions. Every reason it declines — no ladder, a fallback the rule
  // cannot say, a reason two Iris steps both claim — is one the operator drew
  // and will not get, and this list is the only place that is said. Re-deriving
  // it meant a new refusal in `rulesFromFlow` silently stopped being warned
  // about. Flow name and id are irrelevant to WHICH branches survive.
  const written = new Set(rulesFromFlow('', '', steps).map((r) => r.branch));

  for (const step of steps) {
    if (step.type !== 'ai_receptionist') continue;
    for (const key of Object.keys(step.next ?? {})) {
      if (!isHandoffTrigger(key) || written.has(key)) continue;
      out.push(key);
    }
  }
  return out;
}

// ─── Rules → flow ────────────────────────────────────────────────────────────

/**
 * Why a stored rule cannot be drawn on the canvas, or null when it can.
 *
 * Import has to be BEHAVIOUR-PRESERVING or refused. Redrawing a rule without
 * everything it says and then saving makes the loss permanent — a rule that
 * rings an outside mobile would come back as an internal-only ring group, and
 * the next save would write that over the real one. Review, #2880.
 */
export function whyNotImportable(rule: EscalationRule): string | null {
  // Switched off is a DECISION, and a materialised rule is always `enabled:
  // true` — so adopting one would quietly switch it back on the next time
  // anybody saved the flow, undoing that decision with nothing on screen to say
  // so. Preserving the off state instead would be worse: the canvas would show
  // a wired reason that never fires. Review, #2880.
  if (!rule.enabled) return 'is switched off, and drawing it here would turn it back on';
  if (rule.targets.length === 0) return 'rings everyone, so there is nothing to draw';
  if (rule.triggerTypes.length !== 1) {
    return 'covers several reasons at once, and a branch stands for one';
  }
  if (!flowRuleBranch(rule)) return 'uses a reason the canvas does not offer';
  if (rule.targets.some((rung) => rung.externalNumbers.length > 0)) {
    return 'rings an outside number, which a Ring people step cannot hold yet';
  }
  if (rule.phrases.length > 0) {
    return 'matches on its own phrases, which a step cannot carry yet';
  }
  // A scoped rule is refused because the CANVAS CANNOT HOLD THE SCOPE, not
  // because the rule is inert. That distinction matters: a `['voice']` rule
  // fires on calls perfectly well, and an earlier version of this message told
  // operators such a rule never fires — the opposite of the truth. What is
  // actually true of every scoped rule is that a step has nowhere to record
  // `channels`, so a rule the canvas creates is written unscoped: draw one,
  // unwire it, wire it again, and the scope is silently gone. Review, #2880.
  if (rule.channels.length > 0) {
    return `is limited to ${rule.channels.join(' and ')}, and the canvas cannot hold that limit`;
  }
  return null;
}

export function isImportable(rule: EscalationRule): boolean {
  return whyNotImportable(rule) === null;
}

/**
 * A stored rule as the steps that say it in full, ready for the canvas.
 *
 * A HANG-UP FALLBACK IS A STEP, not an implied default. Returning only the ring
 * group left it with no `no_answer` edge, and `fallbackFromStep` reads an
 * unwired outcome as voicemail — so a legacy rule that rang the team and then
 * hung up came back as one that takes a message, and the next save wrote that
 * over the real one. Review, #2880.
 *
 * Voicemail needs no step: it is what an unwired `no_answer` already means, and
 * drawing one would add a node saying what the absence of a node says.
 *
 * `makeId` is called ONCE PER STEP and must not repeat itself. A generator that
 * derives an id from a fixed list of existing steps returns the same one twice,
 * and the hang-up then shares the ring group's id — `no_answer` points the ring
 * step at itself, and the branch resolves to the hangup instead. Review, #2880.
 */
export function stepsFromRule(
  rule: EscalationRule,
  /** Must return a FRESH id on every call — see the collision note below. */
  makeId: (type: CallFlowStep['type']) => string,
): CallFlowStep[] {
  const ring: CallFlowStep = {
    id: makeId('ring_users'),
    type: 'ring_users',
    label: rule.name.replace(FLOW_RULE_PREFIX, ''),
    groups: rule.targets.map((rung) => ({
      notifi2UserIds: [...rung.userIds],
      seconds: clampSeconds(rung.ringSeconds),
    })),
    next: {},
  };

  if (rule.fallback !== 'HANGUP') return [ring];

  const bye: CallFlowStep = { id: makeId('hangup'), type: 'hangup', label: 'Hang up', next: {} };
  ring.next = { no_answer: bye.id };
  return [ring, bye];
}

/**
 * Every reason already answered anywhere in the flow.
 *
 * A reason belongs to the FLOW, not to one Iris step: only one rule per trigger
 * is ever materialised, and `findProblems` blocks two steps naming the same
 * one. Asking a single step what it owns let an import on a second Iris step
 * claim a reason the first already had — authoring the very state the validator
 * refuses to publish. Review, #2880.
 */
export function reasonsWiredInFlow(steps: readonly CallFlowStep[]): string[] {
  const out = new Set<string>();
  for (const step of steps) {
    if (step.type !== 'ai_receptionist') continue;
    for (const key of Object.keys(step.next ?? {})) {
      if ((HANDOFF_TRIGGERS as readonly string[]).includes(key)) out.add(key);
    }
  }
  return [...out];
}

/**
 * The rules an import will ACTUALLY draw, first-come per reason.
 *
 * Shared so the canvas and the claim-by-name that follows it cannot disagree.
 * They did: the importer drew one rule per reason, while the panel renamed
 * every adoptable rule — so with two importable PRICING rules the second was
 * claimed as this flow's without appearing on the canvas, leaving an owned rule
 * nothing represents. Review, #2880.
 */
export function rulesToDraw(
  rules: readonly EscalationRule[],
  alreadyWired: Iterable<string>,
): EscalationRule[] {
  const taken = new Set<string>(alreadyWired);
  const out: EscalationRule[] = [];

  for (const rule of rules) {
    if (!isImportable(rule)) continue;
    const free = branchesForRule(rule).filter((b) => !taken.has(b));
    if (free.length === 0) continue;
    for (const b of free) taken.add(b);
    out.push(rule);
  }
  return out;
}

/** The branches a rule covers, as canvas branch keys. */
export function branchesForRule(rule: EscalationRule): HandoffTrigger[] {
  return rule.triggerTypes
    .map((t) => BRANCH_BY_ESCALATION_TRIGGER[t])
    .filter((b): b is HandoffTrigger => !!b);
}
